/**
 * MapStepNavigationMathHelper
 *
 * A utility class providing mathematical helper functions for step-by-step navigation
 * on maps. This class handles geometric calculations such as vector operations,
 * distance computations, and link/junction detection for navigational purposes.
 *
 * The helper is designed to support pathfinding algorithms that traverse connected
 * navigation links and detect junctions where multiple links intersect.
 */
export default class MapStepNavigationMathHelper {
    /**
     * Calculates the Euclidean length (magnitude) of a 2D vector.
     *
     * @static
     * @param {number} vx - The x-component of the vector
     * @param {number} vy - The y-component of the vector
     * @returns {number} The Euclidean length of the vector
     *
     * @example
     * const length = MapStepNavigationMathHelper.len(3, 4); // Returns 5
     */
    static len(vx, vy) {
        return Math.sqrt(vx * vx + vy * vy);
    }

    /**
     * Normalizes a 2D vector to unit length (magnitude 1).
     *
     * @static
     * @param {number} vx - The x-component of the vector
     * @param {number} vy - The y-component of the vector
     * @returns {Object} An object containing:
     *   - {number} x - The normalized x-component
     *   - {number} y - The normalized y-component
     *   - {number} L - The original length of the vector
     *
     * @example
     * const normalized = MapStepNavigationMathHelper.normalize(3, 4);
     * // Returns { x: 0.6, y: 0.8, L: 5 }
     */
    static normalize(vx, vy) {
        const L = MapStepNavigationMathHelper.len(vx, vy) || 1;
        return { x: vx / L, y: vy / L, L };
    }

    /**
     * Calculates the half-width and padded half-width of a navigation link's corridor.
     *
     * The padding is calculated as 15% of the navigation area or 0.5 units, whichever is larger.
     * This is useful for determining collision zones and navigation corridors.
     *
     * @static
     * @param {Object} link - The navigation link object
     * @param {number} [link.navigationArea] - The navigation area width of the link
     * @returns {Object} An object containing:
     *   - {number} half - Half of the navigation area
     *   - {number} paddedHalf - Half width with additional padding applied
     *
     * @example
     * const widths = MapStepNavigationMathHelper.getLinkHalfWidths({ navigationArea: 10 });
     * // Returns { half: 5, paddedHalf: 6.5 }
     */
    static getLinkHalfWidths(link) {
        const navArea = Number(link?.navigationArea);
        if (!navArea || navArea <= 0) {
            return { half: 0, paddedHalf: 0 };
        }
        const half = navArea / 2;
        const padding = Math.max(0.5, navArea * 0.15);
        return { half, paddedHalf: half + padding };
    }

    /**
     * Calculates the squared distance from a point to a line segment.
     *
     * Uses projection to find the closest point on the segment and returns the squared distance.
     * The squared distance is returned instead of the actual distance for performance optimization.
     *
     * @static
     * @param {number} x - The x-coordinate of the point
     * @param {number} y - The y-coordinate of the point
     * @param {number} ax - The x-coordinate of the segment's start point
     * @param {number} ay - The y-coordinate of the segment's start point
     * @param {number} bx - The x-coordinate of the segment's end point
     * @param {number} by - The y-coordinate of the segment's end point
     * @returns {number} The squared distance from the point to the line segment
     *
     * @example
     * const dist2 = MapStepNavigationMathHelper.distPointToSegment2(0, 0, 0, 5, 5, 5);
     * // Returns 0 (point is on the segment)
     */
    static distPointToSegment2(x, y, ax, ay, bx, by) {
        const abx = bx - ax;
        const aby = by - ay;
        const ab2 = abx * abx + aby * aby;
        if (ab2 === 0) {
            return (x - ax) * (x - ax) + (y - ay) * (y - ay);
        }
        const apx = x - ax;
        const apy = y - ay;
        const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / ab2));
        const qx = ax + t * abx;
        const qy = ay + t * aby;
        const dx = x - qx;
        const dy = y - qy;
        return dx * dx + dy * dy;
    }

    /**
     * Projects a point onto a navigation link and returns projection details.
     *
     * This method finds the closest point on a link (defined by masterItem and slaveItem)
     * to the given point and returns comprehensive projection information including
     * the projected point coordinates, parameter t along the line, and squared distance.
     *
     * @static
     * @param {number} x - The x-coordinate of the point to project
     * @param {number} y - The y-coordinate of the point to project
     * @param {Object} link - The navigation link object
     * @param {Object} link.masterItem - The starting point of the link with x, y coordinates
     * @param {Object} link.slaveItem - The ending point of the link with x, y coordinates
     * @returns {Object|null} An object containing:
     *   - {Object} link - The input link object
     *   - {number} ax, ay - Coordinates of the link's master item
     *   - {number} bx, by - Coordinates of the link's slave item
     *   - {number} abx, aby - Vector from master to slave
     *   - {number} ab2 - Squared length of the link vector
     *   - {number} t - Parameter along the link (0 to 1)
     *   - {number} dist2 - Squared distance from point to projected point
     *   - {number} qx, qy - Coordinates of the projected point
     *   Or null if the link is invalid or has zero length
     *
     * @example
     * const projection = MapStepNavigationMathHelper.projectPointToLink(2.5, 2.5,
     *   { masterItem: { x: 0, y: 0 }, slaveItem: { x: 5, y: 5 } }
     * );
     */
    static projectPointToLink(x, y, link) {
        if (!link || !link.masterItem || !link.slaveItem) return null;
        const ax = link.masterItem.x;
        const ay = link.masterItem.y;
        const bx = link.slaveItem.x;
        const by = link.slaveItem.y;
        // Validate that all coordinate values are valid numbers
        if (typeof ax !== `number` || typeof ay !== `number` || typeof bx !== `number` || typeof by !== `number`) {
            return null;
        }
        // Calculate the vector from master to slave
        const abx = bx - ax;
        const aby = by - ay;
        const ab2 = abx * abx + aby * aby;
        // If the link has zero length, it cannot be used for projection
        if (ab2 === 0) return null;
        // Calculate the projection parameter t using dot product
        const apx = x - ax;
        const apy = y - ay;
        const tRaw = (apx * abx + apy * aby) / ab2;
        // Clamp t to [0, 1] to keep the projection within the line segment
        const t = Math.max(0, Math.min(1, tRaw));
        // Calculate the projected point coordinates
        const qx = ax + t * abx;
        const qy = ay + t * aby;
        // Calculate squared distance from original point to projected point
        const dx = x - qx;
        const dy = y - qy;
        const dist2 = dx * dx + dy * dy; // TODO: replace dist2 with real distance Math.sqrt()
        return { link, ax, ay, bx, by, abx, aby, ab2, t, dist2, qx, qy };
    }

    /**
     * Detects if a point is at a junction where multiple navigation links meet.
     *
     * A junction is defined as a point within the specified radius from one of the link's
     * endpoints (master or slave item) that has at least 2 or more connected links.
     * This is useful for detecting intersection points in a navigation network.
     *
     * @static
     * @param {number} x - The x-coordinate of the point to check
     * @param {number} y - The y-coordinate of the point to check
     * @param {Object} link - The reference navigation link object
     * @param {Object} link.masterItem - The master item with id, x, y properties
     * @param {Object} link.slaveItem - The slave item with id, x, y properties
     * @param {number} radius - The detection radius for junction proximity
     * @param {Array<Object>} movementLinks - Array of all available movement links to search
     * @returns {Object|null} An object containing:
     *   - {string|number} endpointId - The ID of the endpoint where the junction was detected
     *   - {number} x - The x-coordinate of the endpoint
     *   - {number} y - The y-coordinate of the endpoint
     *   Or null if no junction is detected
     *
     * @example
     * const junction = MapStepNavigationMathHelper.detectJunction(
     *   5, 5,
     *   { masterItem: { id: 'a', x: 5, y: 5 }, slaveItem: { id: 'b', x: 10, y: 10 } },
     *   1.5,
     *   movementLinks
     * );
     */
    static detectJunction(x, y, link, radius, movementLinks) {
        if (!link || !link.masterItem || !link.slaveItem) return null;
        const r = Number(radius);
        if (!isFinite(r) || r <= 0) return null;
        if (!Array.isArray(movementLinks)) return null;

        // Pre-calculate squared radius for distance comparison (optimization)
        const r2 = r * r;
        // Extract both endpoints from the link
        const endpoints = [
            { id: link.masterItem.id, x: link.masterItem.x, y: link.masterItem.y },
            { id: link.slaveItem.id, x: link.slaveItem.x, y: link.slaveItem.y },
        ];
        // Check each endpoint to see if it's within the junction detection radius
        for (const ep of endpoints) {
            if (!ep.id || typeof ep.x !== `number` || typeof ep.y !== `number`) continue;
            const dx = x - ep.x;
            const dy = y - ep.y;
            // Skip if the point is outside the radius of this endpoint
            if ((dx * dx + dy * dy) > r2) continue;

            // Count how many links are connected to this endpoint
            let count = 0;
            for (const candidateLink of movementLinks) {
                if (candidateLink?.masterItem?.id === ep.id || candidateLink?.slaveItem?.id === ep.id) {
                    count++;
                    // Early exit once we've found 2 or more links (junction condition met)
                    if (count >= 2) break;
                }
            }
            // Return the endpoint if it's a junction (2+ connected links)
            if (count >= 2) {
                return { endpointId: ep.id, x: ep.x, y: ep.y };
            }
        }
        return null;
    }

    /**
     * Selects the best navigation link from a junction based on movement direction.
     *
     * Evaluates all links connected to the specified endpoint and scores them based on
     * alignment with the provided normalized movement vector. Prioritizes links with
     * positive scores (forward direction), and falls back to the highest-scoring link
     * if no positive score exists.
     *
     * @static
     * @param {string|number} endpointId - The ID of the endpoint/junction node
     * @param {Object} mvNorm - A normalized movement vector with x and y components
     * @param {number} mvNorm.x - The normalized x-component of the movement direction
     * @param {number} mvNorm.y - The normalized y-component of the movement direction
     * @param {Array<Object>} movementLinks - Array of all available navigation links
     * @returns {Object|null} An object containing:
     *   - {Object} link - The selected navigation link
     *   - {number} score - The directional alignment score for this link
     *   Or null if no valid links are found or input is invalid
     *
     * @example
     * const best = MapStepNavigationMathHelper.getBestLinkFromJunction(
     *   'junctionA',
     *   { x: 0.707, y: 0.707 },
     *   movementLinks
     * );
     */
    static getBestLinkFromJunction(endpointId, mvNorm, movementLinks) {
        if (!endpointId || !Array.isArray(movementLinks)) return null;

        let bestPositive = null;
        let bestOverall = null;
        for (const link of movementLinks) {
            if (!link || !link.masterItem || !link.slaveItem) continue;
            // Check if this link is connected to the endpoint
            if (!(link.masterItem.id === endpointId || link.slaveItem.id === endpointId)) continue;

            // Determine the direction vector from the endpoint to the other end of the link
            const from = (link.masterItem.id === endpointId) ? link.masterItem : link.slaveItem;
            const to = (link.masterItem.id === endpointId) ? link.slaveItem : link.masterItem;
            const vx = to.x - from.x;
            const vy = to.y - from.y;
            // Normalize the link direction vector
            const vlen = Math.sqrt(vx * vx + vy * vy) || 1;
            const dirx = vx / vlen;
            const diry = vy / vlen;
            // Calculate dot product: score indicates how aligned this link is with movement direction
            const score = dirx * mvNorm.x + diry * mvNorm.y;

            const candidate = { link, score };
            // Track the best link with positive alignment (forward direction)
            if (score > 0 && (!bestPositive || score > bestPositive.score)) {
                bestPositive = candidate;
            }
            // Track the best link overall (regardless of direction)
            if (!bestOverall || score > bestOverall.score) {
                bestOverall = candidate;
            }
        }

        // Prefer links with positive alignment, fallback to best overall
        return bestPositive || bestOverall;
    }

    /**
     * Chooses the next navigation link from a junction with advanced scoring criteria.
     *
     * Evaluates available links connected to an endpoint and ranks them using:
     * - Base score: directional alignment with the movement vector
     * - Corridor bonus: whether the point is within the link's navigation corridor
     * Preferences scoring criteria: positive directional alignment, then corridor presence,
     * then highest overall score.
     *
     * @static
     * @param {string|number} endpointId - The ID of the endpoint/junction node
     * @param {number} px - The x-coordinate of the current position
     * @param {number} py - The y-coordinate of the current position
     * @param {Object} mvNorm - A normalized movement vector with x and y components
     * @param {number} mvNorm.x - The normalized x-component of the movement direction
     * @param {number} mvNorm.y - The normalized y-component of the movement direction
     * @param {Object} excludeLink - A link object to exclude from consideration (optional)
     * @param {Array<Object>} movementLinks - Array of all available navigation links
     * @returns {Object|null} An object containing:
     *   - {Object} link - The selected navigation link
     *   - {number} score - The combined score (baseScore + corridorBonus)
     *   - {number} baseScore - The directional alignment component
     *   - {number} corridorBonus - The corridor presence bonus (0 or 1)
     *   Or null if no valid links are found or input is invalid
     *
     * @example
     * const next = MapStepNavigationMathHelper.chooseNextLink(
     *   'junctionA',
     *   10,
     *   20,
     *   { x: 0.6, y: 0.8 },
     *   excludeLink,
     *   movementLinks
     * );
     */
    static chooseNextLink(endpointId, px, py, mvNorm, excludeLink, movementLinks) {
        if (!endpointId || !Array.isArray(movementLinks)) return null;

        const candidates = [];
        for (const link of movementLinks) {
            // Skip the link we want to exclude
            if (link === excludeLink) continue;
            try {
                // Check if this link is connected to the endpoint
                if (!(link.masterItem?.id === endpointId || link.slaveItem?.id === endpointId)) continue;

                // Calculate the direction vector from the endpoint along the link
                const lax = (link.masterItem?.id === endpointId) ? link.slaveItem.x - link.masterItem.x : link.masterItem.x - link.slaveItem.x;
                const lay = (link.masterItem?.id === endpointId) ? link.slaveItem.y - link.masterItem.y : link.masterItem.y - link.slaveItem.y;
                // Normalize the link direction
                const llen = Math.sqrt(lax * lax + lay * lay) || 1;
                const dirx = lax / llen;
                const diry = lay / llen;

                // Base score: alignment with movement direction
                const baseScore = dirx * mvNorm.x + diry * mvNorm.y;
                // Get the navigation corridor width for this link
                const { paddedHalf } = MapStepNavigationMathHelper.getLinkHalfWidths(link);

                // Bonus: check if current position is within the link's corridor
                let corridorBonus = 0;
                if (paddedHalf > 0) {
                    const ax = link.masterItem.x;
                    const ay = link.masterItem.y;
                    const bx = link.slaveItem.x;
                    const by = link.slaveItem.y;
                    // Calculate distance from current position to the link
                    const d2 = MapStepNavigationMathHelper.distPointToSegment2(px, py, ax, ay, bx, by);
                    // Grant bonus if position is within the padded corridor
                    if (d2 <= paddedHalf * paddedHalf + 1e-9) corridorBonus = 1;
                }

                // Combined score: directional alignment + corridor presence
                const score = baseScore + corridorBonus;
                candidates.push({ link, score, baseScore, corridorBonus });
            } catch (e) {
                continue;
            }
        }

        if (!candidates.length) return null;
        // Sort candidates by score (highest first)
        candidates.sort((a, b) => b.score - a.score);
        // Prefer: positive score > corridor bonus > highest overall score
        return candidates.find(c => c.score > 0.05) || candidates.find(c => c.corridorBonus > 0) || candidates[0];
    }

    /**
     * Finds the best projection of a point onto available navigation links.
     *
     * Evaluates all links in the movement links array and finds the closest projection.
     * Prioritizes projections that fall within the link's navigation corridor (using 1/3
     * of the navigation area as the threshold). Falls back to the closest projection if
     * none are found inside a corridor. Excludes links marked as unavailable.
     *
     * @static
     * @param {number} x - The x-coordinate of the point to project
     * @param {number} y - The y-coordinate of the point to project
     * @param {Array<Object>} movementLinks - Array of all available navigation links
     * @returns {Object|null} An object containing:
     *   - {Object} link - The navigation link the point was projected onto
     *   - {number} ax, ay - Coordinates of the link's master item
     *   - {number} bx, by - Coordinates of the link's slave item
     *   - {number} abx, aby - Vector from master to slave
     *   - {number} ab2 - Squared length of the link vector
     *   - {number} t - Parameter along the link (0 to 1, where 0 is master and 1 is slave)
     *   - {number} dist2 - Squared distance from the original point to the projection
     *   - {number} qx, qy - Coordinates of the projected point on the link
     *   - {number} half - Half of the navigation area (using 1/3 for corridor threshold)
     *   Or null if no valid links are found or input is invalid
     *
     * Algorithm preference:
     * 1. Returns the closest projection that falls inside a corridor (bestInside)
     * 2. Falls back to the closest projection overall (bestFallback)
     *
     * @example
     * const projection = MapStepNavigationMathHelper.findBestProjection(
     *   15,
     *   25,
     *   movementLinks
     * );
     */
    static findBestProjection(x, y, movementLinks) {
        if (!Array.isArray(movementLinks)) return null;

        let bestInside = null;
        let bestFallback = null;

        // Iterate through all available links to find the best projection
        for (const link of movementLinks) {
            try {
                // Skip links that are marked as unavailable
                if (link.navRule && link.navRule === `unavailable`) {
                    continue;
                }

                // Project the point onto the current link
                const projected = MapStepNavigationMathHelper.projectPointToLink(x, y, link);
                if (!projected) continue;

                // Calculate the corridor threshold (using 1/3 of the navigation area)
                const navArea = Number(link.navigationArea);
                const half = navArea > 0 ? navArea / 3 : 0;
                // Check if the projection is inside the corridor
                const inside = half > 0 && projected.dist2 <= (half * half + 1e-9);
                const pack = { ...projected, half };

                // Track the closest projection overall
                if (!bestFallback || projected.dist2 < bestFallback.dist2) {
                    bestFallback = pack;
                }

                // Track the closest projection that is inside a corridor
                if (inside && (!bestInside || projected.dist2 < bestInside.dist2)) {
                    bestInside = pack;
                }
            } catch (e) {
                continue;
            }
        }

        // Prefer projections inside a corridor, otherwise return the closest overall
        return bestInside || bestFallback;
    }
}
